import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import GiftIcon from "@redotech/redo-icons/gift-01.svg";
import PackageCheckIcon from "@redotech/redo-icons/package-check.svg";
import RepeatIcon from "@redotech/redo-icons/repeat-03.svg";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-01.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { JSXElementConstructor, memo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { OnboardingProps } from "../common";
import polyMailersHeroUrl from "./supplies-product-announcement-assets/poly-mailers-hero.png?url";
import * as styles from "./supplies-product-announcement-modal.module.css";

const BENEFITS: {
  Icon: JSXElementConstructor<{ className?: string }>;
  isOffer?: boolean;
  label: string;
}[] = [
  { Icon: RepeatIcon, label: "Set up recurring restocks that fit your needs" },
  {
    Icon: PackageCheckIcon,
    label: "OMS merchants can reorder automatically based on supply levels",
  },
  { Icon: ShoppingBagIcon, label: "Special member pricing with zero setup" },
  {
    Icon: GiftIcon,
    isOffer: true,
    label: "JULY ONLY: free box of labels when you spend $200",
  },
];

const SUPPLIES_HELP_ARTICLE_URL =
  "https://help.getredo.com/en/articles/16056388-redo-supplies";

export const SuppliesProductAnnouncementModal = memo(
  function SuppliesProductAnnouncementModal({
    handleDismiss,
    handleComplete,
  }: OnboardingProps) {
    const team = useContext(TeamContext);
    const reloadTeam = useContext(ReloadTeamContext);
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const navigate = useNavigate();

    const handleLearnMore = () => {
      window.open(SUPPLIES_HELP_ARTICLE_URL, "_blank", "noopener,noreferrer");
    };

    // Enable the storefront (same as "Shop now"); the base route forwards to
    // the shop once the reloaded team reflects the enabled storefront.
    const [exploreState, triggerExplore] = useTriggerLoad(async () => {
      await rpcClient.enableSuppliesStorefront({});
      reloadTeam?.();
      if (team) {
        navigate(`/stores/${team._id.toString()}/redo-supplies`);
      }
      await handleComplete();
      return null;
    });

    return (
      <RedoBaseModal
        darkMode
        isOpen
        onModalCloseRequested={handleDismiss}
        otherClasses={[styles.modal]}
      >
        <div className={styles.shell}>
          <button
            aria-label="Close"
            className={styles.closeButton}
            onClick={handleDismiss}
            type="button"
          >
            <XCloseIcon />
          </button>
          <div className={styles.content}>
            <p className={styles.eyebrow}>Redo Supplies</p>
            <header className={styles.heading}>
              <h2 className={styles.title}>Supplies that reorder themselves</h2>
              <p className={styles.bodyText}>
                If it&apos;s in your warehouse, we stock it. Whether you need
                boxes, poly mailers, or tape, order through Redo to take
                advantage of special member pricing and benefits.
              </p>
            </header>

            <section className={styles.benefitsSection}>
              <p className={styles.sectionLabel}>MEMBER BENEFITS:</p>
              <div className={styles.benefits}>
                {BENEFITS.map(({ Icon, isOffer, label }) => (
                  <div
                    className={
                      isOffer
                        ? `${styles.benefitCard} ${styles.offerCard}`
                        : styles.benefitCard
                    }
                    key={label}
                  >
                    <span
                      className={
                        isOffer
                          ? `${styles.benefitIcon} ${styles.offerIcon}`
                          : styles.benefitIcon
                      }
                    >
                      <Icon />
                    </span>
                    <span className={styles.benefitLabel}>{label}</span>
                  </div>
                ))}
              </div>
            </section>

            <div className={styles.actions}>
              <RedoButton
                hierarchy="secondary"
                onClick={handleLearnMore}
                size="sm"
                text="Learn more"
              />
              <RedoButton
                hierarchy="primary"
                onClick={() => triggerExplore()}
                pending={exploreState.pending}
                size="sm"
                text="Start shopping"
              />
            </div>
          </div>
          <div className={styles.imagePanel}>
            <img
              alt="Poly mailers"
              className={styles.image}
              src={polyMailersHeroUrl}
            />
          </div>
        </div>
      </RedoBaseModal>
    );
  },
);
